import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Users, Plus, Edit, Trash2, DollarSign, History, RefreshCw, Check, X, Image, ArrowDownCircle, ArrowUpCircle, Eye, EyeOff, KeyRound, Landmark, Save, Bell, Send } from 'lucide-react';

const API = '/api';

const TYPE_LABELS = { founder: 'شريك مؤسس', investor: 'شريك مستثمر' };
const TYPE_COLORS = {
  founder: 'bg-purple-100 text-purple-700 border-purple-300',
  investor: 'bg-emerald-100 text-emerald-700 border-emerald-300',
};

export default function PartnersAndProfitsPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [partners, setPartners] = useState([]);
  const [distHistory, setDistHistory] = useState([]);
  const [allRequests, setAllRequests] = useState({ deposits: [], withdrawals: [] });
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [imageViewUrl, setImageViewUrl] = useState(null);
  const [editPartner, setEditPartner] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);

  const [distYear, setDistYear] = useState(new Date().getFullYear());
  const [distMonth, setDistMonth] = useState(new Date().getMonth() + 1);
  const [distTotal, setDistTotal] = useState('');
  const [investorPcts, setInvestorPcts] = useState({});

  // Capital tab state
  const [capYear, setCapYear] = useState(new Date().getFullYear());
  const [capMonth, setCapMonth] = useState(new Date().getMonth() + 1);
  const [capEntries, setCapEntries] = useState({});
  const [capSaved, setCapSaved] = useState(null);
  const [capSaving, setCapSaving] = useState(false);
  const [capLoading, setCapLoading] = useState(false);

  // Records management state
  const [selectedPartnerRecords, setSelectedPartnerRecords] = useState('');
  const [recordTypeFilter, setRecordTypeFilter] = useState('all');
  const [partnerRecords, setPartnerRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [addRecordOpen, setAddRecordOpen] = useState(false);
  const [editRecordId, setEditRecordId] = useState(null);
  const [editRecordAmount, setEditRecordAmount] = useState('');

  // Notifications state
  const [allNotifications, setAllNotifications] = useState([]);
  const [notifPartner, setNotifPartner] = useState('');
  const [notifMessage, setNotifMessage] = useState('');
  const [notifType, setNotifType] = useState('general');
  const [notifSending, setNotifSending] = useState(false);
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [broadcastType, setBroadcastType] = useState('general');

  // Individual profit state
  const [profitPartner, setProfitPartner] = useState('');
  const [profitAmount, setProfitAmount] = useState('');
  const [profitYear, setProfitYear] = useState(new Date().getFullYear());
  const [profitMonth, setProfitMonth] = useState(new Date().getMonth() + 1);
  const [profitSendNotif, setProfitSendNotif] = useState(true);
  const [profitNotifType, setProfitNotifType] = useState('profit');
  const [profitSaving, setProfitSaving] = useState(false);

  const [editingCell, setEditingCell] = useState(null); // {partnerId, field}
  const [editingValue, setEditingValue] = useState('');

  const startCellEdit = (partnerId, field, currentValue) => {
    setEditingCell({ partnerId, field });
    setEditingValue(String(currentValue || 0));
  };

  const saveCellEdit = async () => {
    if (!editingCell) return;
    const { partnerId, field } = editingCell;
    const val = parseFloat(editingValue) || 0;
    try {
      await axios.patch(`${API}/partners/${partnerId}/update`, { [field]: val }, { headers });
      setEditingCell(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل التحديث');
    }
  };

  const handleCellKeyDown = (e) => {
    if (e.key === 'Enter') saveCellEdit();
    if (e.key === 'Escape') setEditingCell(null);
  };

  const renderEditableCell = (p, field, colorClass) => {
    const isEditing = editingCell?.partnerId === p.id && editingCell?.field === field;
    const value = p[field] || 0;
    if (isEditing) {
      return (
        <Input type="number" step="0.01" autoFocus className="w-28 h-8 text-center"
          value={editingValue} onChange={e => setEditingValue(e.target.value)}
          onKeyDown={handleCellKeyDown} onBlur={saveCellEdit}
          data-testid={`edit-cell-${field}-${p.id}`} />
      );
    }
    return (
      <div className="flex items-center gap-1 cursor-pointer group" onClick={() => startCellEdit(p.id, field, value)}
        title="انقر للتعديل" data-testid={`cell-${field}-${p.id}`}>
        <span className={`${colorClass} font-bold`}>{value.toLocaleString()}</span>
        <Edit className="h-3 w-3 text-slate-300 group-hover:text-slate-500 transition-colors" />
      </div>
    );
  };

  const fetchData = useCallback(async () => {
    try {
      const [pRes, hRes, rRes] = await Promise.all([
        axios.get(`${API}/partners/simple`, { headers }),
        axios.get(`${API}/distributions/history`, { headers }),
        axios.get(`${API}/admin/all-requests`, { headers }),
      ]);
      setPartners(pRes.data || []);
      setDistHistory(hRes.data || []);
      setAllRequests(rRes.data || { deposits: [], withdrawals: [] });
    } catch (error) {
      toast.error('فشل تحميل البيانات');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAddPartner = async (e) => {
    e.preventDefault();
    setSaving(true);
    const fd = new FormData(e.target);
    try {
      await axios.post(`${API}/partners/add`, {
        username: fd.get('username'),
        password: fd.get('password'),
        full_name: fd.get('full_name'),
        initial_capital: parseFloat(fd.get('initial_capital')) || 0,
        profit_percentage: parseFloat(fd.get('profit_percentage')) || 0,
        profit_start_date: fd.get('profit_start_date') || undefined,
        partner_type: fd.get('partner_type'),
      }, { headers });
      toast.success('تم إضافة الشريك');
      setAddOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل إضافة الشريك');
    } finally { setSaving(false); }
  };

  const handleEditPartner = async (e) => {
    e.preventDefault();
    if (!editPartner) return;
    setSaving(true);
    const fd = new FormData(e.target);
    const body = {
      full_name: fd.get('full_name'),
      initial_capital: parseFloat(fd.get('initial_capital')) || 0,
      profit_percentage: parseFloat(fd.get('profit_percentage')) || 0,
      profit_start_date: fd.get('profit_start_date') || undefined,
      partner_type: fd.get('partner_type'),
      username: fd.get('username'),
    };
    const pw = fd.get('new_password');
    if (pw && pw.trim().length > 0) body.password = pw;
    try {
      await axios.patch(`${API}/partners/${editPartner.id}/update`, body, { headers });
      toast.success('تم تحديث بيانات الشريك بنجاح');
      setEditOpen(false);
      setEditPartner(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل التحديث');
    } finally { setSaving(false); }
  };

  const handleDeletePartner = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا الشريك؟')) return;
    try { await axios.delete(`${API}/partners/${id}`, { headers }); toast.success('تم حذف الشريك'); fetchData(); }
    catch { toast.error('فشل الحذف'); }
  };

  // Records management
  const fetchPartnerRecords = async (partnerId, type) => {
    if (!partnerId) return;
    setRecordsLoading(true);
    try {
      const res = await axios.get(`${API}/records/partner/${partnerId}`, { headers, params: { record_type: type || recordTypeFilter } });
      setPartnerRecords(res.data || []);
    } catch { toast.error('فشل تحميل السجلات'); }
    finally { setRecordsLoading(false); }
  };

  useEffect(() => {
    if (selectedPartnerRecords) fetchPartnerRecords(selectedPartnerRecords, recordTypeFilter);
  }, [selectedPartnerRecords, recordTypeFilter]);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications`, { headers });
      setAllNotifications(res.data || []);
    } catch {}
  };

  useEffect(() => { fetchNotifications(); }, []);

  const handleAddRecord = async (e) => {
    e.preventDefault();
    const form = new FormData(e.target);
    try {
      await axios.post(`${API}/distributions/manual-entry`, {
        partner_id: selectedPartnerRecords,
        amount: parseFloat(form.get('amount')),
        year: parseInt(form.get('year')),
        month: parseInt(form.get('month')),
        description: form.get('description') || undefined,
      }, { headers });
      toast.success('تم إضافة السجل');
      setAddRecordOpen(false);
      fetchPartnerRecords(selectedPartnerRecords);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'فشل الإضافة'); }
  };

  const handleUpdateRecord = async (recordId) => {
    try {
      await axios.patch(`${API}/distributions/${recordId}`, {
        amount: parseFloat(editRecordAmount)
      }, { headers });
      toast.success('تم التحديث');
      setEditRecordId(null);
      fetchPartnerRecords(selectedPartnerRecords);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'فشل التحديث'); }
  };

  const handleDeleteRecord = async (record) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا السجل؟')) return;
    try {
      const urlMap = { profit: `distributions/${record.id}`, deposit: `deposit-requests/${record.id}`, withdrawal: `withdrawal-requests/${record.id}` };
      await axios.delete(`${API}/${urlMap[record.record_type]}`, { headers });
      toast.success('تم حذف السجل');
      fetchPartnerRecords(selectedPartnerRecords, recordTypeFilter);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'فشل الحذف'); }
  };

  const handleSendNotification = async () => {
    if (!notifPartner || !notifMessage.trim()) return;
    setNotifSending(true);
    try {
      await axios.post(`${API}/notifications`, { partner_id: notifPartner, message: notifMessage.trim(), notification_type: notifType }, { headers });
      toast.success('تم إرسال الإشعار');
      setNotifMessage('');
      fetchNotifications();
    } catch (error) { toast.error(error.response?.data?.detail || 'فشل الإرسال'); }
    finally { setNotifSending(false); }
  };

  const handleBroadcast = async () => {
    if (!broadcastMessage.trim()) return;
    setNotifSending(true);
    try {
      await axios.post(`${API}/notifications/broadcast`, { message: broadcastMessage.trim(), notification_type: broadcastType }, { headers });
      toast.success('تم إرسال الإشعار للجميع');
      setBroadcastMessage('');
      fetchNotifications();
    } catch (error) { toast.error(error.response?.data?.detail || 'فشل الإرسال'); }
    finally { setNotifSending(false); }
  };

  const handleDeleteNotification = async (id) => {
    try {
      await axios.delete(`${API}/notifications/${id}`, { headers });
      fetchNotifications();
    } catch { toast.error('فشل الحذف'); }
  };

  // Individual profit addition
  const handleAddIndividualProfit = async () => {
    if (!profitPartner || !profitAmount || parseFloat(profitAmount) <= 0) {
      toast.error('يرجى اختيار الشريك وإدخال المبلغ');
      return;
    }
    setProfitSaving(true);
    try {
      await axios.post(`${API}/distributions/manual-entry`, {
        partner_id: profitPartner,
        amount: parseFloat(profitAmount),
        year: profitYear,
        month: profitMonth,
        send_notification: profitSendNotif,
        notification_type: profitNotifType,
      }, { headers });
      toast.success('تم إضافة الربح بنجاح');
      setProfitAmount('');
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'فشل الإضافة'); }
    finally { setProfitSaving(false); }
  };

  const handleDistribute = async () => {
    if (!distTotal || parseFloat(distTotal) <= 0) { toast.error('يرجى إدخال مبلغ الربح'); return; }
    setSaving(true);
    try {
      // Always send ALL investor percentages (edited or stored default)
      const allInvestorPcts = {};
      investors.forEach(inv => {
        allInvestorPcts[inv.id] = investorPcts[inv.id] !== undefined ? investorPcts[inv.id] : inv.profit_percentage;
      });

      const res = await axios.post(`${API}/distributions/create`, {
        year: distYear, month: distMonth,
        total_profit: parseFloat(distTotal),
        investor_percentages: Object.keys(allInvestorPcts).length > 0 ? allInvestorPcts : undefined,
      }, { headers });
      toast.success(res.data.message);
      setDistTotal(''); setInvestorPcts({}); fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل التوزيع');
    } finally { setSaving(false); }
  };

  const handleRequestAction = async (type, requestId, status) => {
    try {
      await axios.patch(`${API}/admin/${type}-requests/${requestId}`, { status }, { headers });
      toast.success(status === 'approved' ? 'تم قبول الطلب' : 'تم رفض الطلب');
      fetchData();
    } catch { toast.error('فشل تحديث الطلب'); }
  };

  const handleViewImage = async (imagePath) => {
    try {
      const res = await axios.get(`${API}/files/${imagePath}`, { headers, responseType: 'blob' });
      setImageViewUrl(URL.createObjectURL(res.data));
    } catch { toast.error('فشل تحميل الصورة'); }
  };

  const fetchCapitalSnapshot = useCallback(async (y, m) => {
    setCapLoading(true);
    try {
      const res = await axios.get(`${API}/capital-snapshots`, { headers, params: { year: y, month: m } });
      const entries = res.data?.entries || {};
      const restored = {};
      Object.keys(entries).forEach(pid => {
        restored[pid] = entries[pid]?.capital ?? entries[pid] ?? 0;
      });
      setCapEntries(restored);
      setCapSaved(res.data?.total_capital ? res.data : null);
    } catch {
      setCapEntries({});
      setCapSaved(null);
    } finally { setCapLoading(false); }
  }, []);

  useEffect(() => { fetchCapitalSnapshot(capYear, capMonth); }, [capYear, capMonth, partners]);

  const handleSaveCapital = async () => {
    setCapSaving(true);
    try {
      const res = await axios.post(`${API}/capital-snapshots`, {
        year: capYear, month: capMonth, entries: capEntries
      }, { headers });
      toast.success(res.data.message);
      setCapSaved(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل حفظ بيانات رأس المال');
    } finally { setCapSaving(false); }
  };

  // Derived data
  const founders = partners.filter(p => p.partner_type === 'founder');
  const investors = partners.filter(p => p.partner_type === 'investor');
  const monthNames = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
  const pendingDeposits = allRequests.deposits?.filter(r => r.status === 'pending') || [];
  const pendingWithdrawals = allRequests.withdrawals?.filter(r => r.status === 'pending') || [];

  if (loading) return <div className="min-h-screen flex items-center justify-center" dir="rtl"><RefreshCw className="h-8 w-8 animate-spin text-emerald-600" /></div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30 p-6" dir="rtl" data-testid="partners-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-black text-emerald-800 flex items-center gap-3"><Users className="h-8 w-8" />الشركاء والأرباح</h1>
            <p className="text-slate-500 mt-1">إدارة الشركاء وتوزيع الأرباح والطلبات</p>
          </div>
          <Button variant="outline" onClick={() => navigate('/admin')} className="rounded-full"><ArrowLeft className="ml-2 h-4 w-4" />العودة</Button>
        </div>

        <Tabs defaultValue="partners" className="space-y-6">
          <TabsList className="bg-white border p-1 rounded-full gap-1">
            <TabsTrigger value="partners" className="rounded-full data-[state=active]:bg-emerald-600 data-[state=active]:text-white font-bold px-5">الشركاء ({partners.length})</TabsTrigger>
            <TabsTrigger value="distribute" className="rounded-full data-[state=active]:bg-emerald-600 data-[state=active]:text-white font-bold px-5">توزيع الأرباح</TabsTrigger>
            <TabsTrigger value="requests" className="rounded-full data-[state=active]:bg-emerald-600 data-[state=active]:text-white font-bold px-5">
              الطلبات {(pendingDeposits.length + pendingWithdrawals.length) > 0 && <span className="mr-1 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center">{pendingDeposits.length + pendingWithdrawals.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="history" className="rounded-full data-[state=active]:bg-emerald-600 data-[state=active]:text-white font-bold px-5">السجل</TabsTrigger>
            <TabsTrigger value="capital" className="rounded-full data-[state=active]:bg-emerald-600 data-[state=active]:text-white font-bold px-5" data-testid="capital-tab">رأس المال</TabsTrigger>
            <TabsTrigger value="notifications" className="rounded-full data-[state=active]:bg-emerald-600 data-[state=active]:text-white font-bold px-5" data-testid="notifications-tab">الإشعارات</TabsTrigger>
            <TabsTrigger value="add-profit" className="rounded-full data-[state=active]:bg-emerald-600 data-[state=active]:text-white font-bold px-5" data-testid="add-profit-tab">إضافة ربح</TabsTrigger>
          </TabsList>

          {/* ========== PARTNERS TAB ========== */}
          <TabsContent value="partners">
            <Card className="bg-white shadow-lg border-0">
              <CardHeader className="flex flex-row items-center justify-between">
                <div className="flex items-center gap-3">
                  <CardTitle className="text-lg">قائمة الشركاء</CardTitle>
                  <Button size="sm" variant="ghost" onClick={() => setShowPasswords(!showPasswords)} data-testid="toggle-passwords" title="إظهار/إخفاء كلمات المرور">
                    {showPasswords ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                <Dialog open={addOpen} onOpenChange={setAddOpen}>
                  <DialogTrigger asChild>
                    <Button className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="add-partner-btn"><Plus className="ml-2 h-4 w-4" />إضافة شريك</Button>
                  </DialogTrigger>
                  <DialogContent dir="rtl" className="max-w-md max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>إضافة شريك جديد</DialogTitle></DialogHeader>
                    <form onSubmit={handleAddPartner} className="space-y-3">
                      <div><Label>الاسم الكامل</Label><Input name="full_name" required className="mt-1" data-testid="add-fullname" /></div>
                      <div><Label>اسم المستخدم</Label><Input name="username" required className="mt-1" data-testid="add-username" /></div>
                      <div><Label>كلمة المرور</Label><Input name="password" required className="mt-1" data-testid="add-password" /></div>
                      <div>
                        <Label>نوع الشريك</Label>
                        <select name="partner_type" className="w-full mt-1 h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="add-partner-type">
                          <option value="founder">شريك مؤسس</option>
                          <option value="investor">شريك مستثمر</option>
                        </select>
                      </div>
                      <div><Label>رأس المال</Label><Input name="initial_capital" type="number" step="0.01" defaultValue="0" className="mt-1" /></div>
                      <div>
                        <Label>النسبة (%) <span className="text-xs text-slate-400">- للمستثمرين فقط</span></Label>
                        <Input name="profit_percentage" type="number" step="0.01" defaultValue="0" className="mt-1" />
                      </div>
                      <div><Label>تاريخ بداية الأرباح</Label><Input name="profit_start_date" type="date" className="mt-1" /></div>
                      <Button type="submit" disabled={saving} className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="submit-add-partner">
                        {saving ? 'جاري الحفظ...' : 'إضافة الشريك'}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {/* Fixed structure */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-4 text-sm">
                  <p className="font-bold text-slate-700 mb-2">هيكل التوزيع الثابت:</p>
                  <p className="text-slate-600">مؤسس 1 (25%) + مؤسس 2 (25%) + محفظة مستثمرين (50%) → مؤسس 3 (30% من المحفظة) + المستثمرون (70% من المحفظة)</p>
                </div>

                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-right font-bold">الاسم</TableHead>
                        <TableHead className="text-right font-bold">النوع</TableHead>
                        <TableHead className="text-right font-bold">اسم المستخدم</TableHead>
                        {showPasswords && <TableHead className="text-right font-bold">كلمة المرور</TableHead>}
                        <TableHead className="text-right font-bold">الرصيد</TableHead>
                        <TableHead className="text-right font-bold">ربح آخر شهر</TableHead>
                        <TableHead className="text-right font-bold">إجراءات</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {partners.map(p => (
                        <TableRow key={p.id} data-testid="partner-row">
                          <TableCell className="font-bold">{p.full_name}</TableCell>
                          <TableCell><Badge className={`border ${TYPE_COLORS[p.partner_type] || TYPE_COLORS.investor}`}>{TYPE_LABELS[p.partner_type] || 'مستثمر'}</Badge></TableCell>
                          <TableCell className="font-mono text-sm">{p.username}</TableCell>
                          {showPasswords && <TableCell className="font-mono text-sm">{p.password}</TableCell>}
                          <TableCell>{renderEditableCell(p, 'balance', 'text-slate-700')}</TableCell>
                          <TableCell>{renderEditableCell(p, 'last_month_profit', 'text-amber-600')}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" data-testid={`edit-${p.id}`} onClick={() => { setEditPartner(p); setEditOpen(true); }}><Edit className="h-4 w-4" /></Button>
                              <Button size="sm" variant="ghost" className="text-red-500" data-testid={`delete-${p.id}`} onClick={() => handleDeletePartner(p.id)}><Trash2 className="h-4 w-4" /></Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ========== DISTRIBUTION TAB ========== */}
          <TabsContent value="distribute">
            <Card className="bg-white shadow-lg border-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg"><DollarSign className="h-5 w-5 text-emerald-600" />توزيع الأرباح الشهري</CardTitle>
                <CardDescription>مؤسس 1 (25%) + مؤسس 2 (25%) + محفظة (50%) → مؤسس 3 (30% من المحفظة) + مستثمرون (70%)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-3 gap-4">
                  <div><Label>السنة</Label><Input type="number" value={distYear} onChange={e => setDistYear(parseInt(e.target.value))} className="mt-1" /></div>
                  <div>
                    <Label>الشهر</Label>
                    <select value={distMonth} onChange={e => setDistMonth(parseInt(e.target.value))} className="w-full mt-1 h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="dist-month">
                      {monthNames.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                    </select>
                  </div>
                  <div><Label>إجمالي الربح (ج.م)</Label><Input type="number" step="0.01" value={distTotal} onChange={e => setDistTotal(e.target.value)} placeholder="أدخل إجمالي الربح" className="mt-1" data-testid="dist-total" /></div>
                </div>

                {/* Preview */}
                {distTotal && parseFloat(distTotal) > 0 && (() => {
                  const total = parseFloat(distTotal);
                  const wallet = total * 0.5;
                  const f3Amount = wallet * 0.3;
                  const remaining = wallet * 0.7;

                  return (
                    <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                      <h3 className="font-bold text-slate-700">معاينة التوزيع:</h3>
                      {founders.length > 0 && (
                        <div className="flex justify-between items-center py-2 border-b border-slate-200">
                          <span>{founders[0].full_name} <Badge className="border bg-purple-100 text-purple-700 border-purple-300 mr-2">مؤسس 1</Badge></span>
                          <span className="font-bold text-purple-600">{(total * 0.25).toLocaleString(undefined, {maximumFractionDigits: 2})} ج.م (25%)</span>
                        </div>
                      )}
                      {founders.length > 1 && (
                        <div className="flex justify-between items-center py-2 border-b border-slate-200">
                          <span>{founders[1].full_name} <Badge className="border bg-indigo-100 text-indigo-700 border-indigo-300 mr-2">مؤسس 2</Badge></span>
                          <span className="font-bold text-indigo-600">{(total * 0.25).toLocaleString(undefined, {maximumFractionDigits: 2})} ج.م (25%)</span>
                        </div>
                      )}
                      <div className="flex justify-between py-2 bg-blue-50 px-3 rounded border border-blue-200">
                        <span className="font-bold text-blue-700">محفظة المستثمرين (50%)</span>
                        <span className="font-bold text-blue-700">{wallet.toLocaleString(undefined, {maximumFractionDigits: 2})} ج.م</span>
                      </div>
                      {founders.length > 2 && (
                        <div className="flex justify-between items-center py-2 border-b border-slate-200 pr-4">
                          <span>{founders[2].full_name} <Badge className="border bg-blue-100 text-blue-700 border-blue-300 mr-2">مؤسس 3</Badge></span>
                          <span className="font-bold text-blue-600">{f3Amount.toLocaleString(undefined, {maximumFractionDigits: 2})} ج.م (30% من المحفظة)</span>
                        </div>
                      )}
                      {investors.length > 0 && (
                        <div className="pr-4 space-y-2">
                          <div className="flex justify-between items-center">
                            <p className="text-sm font-bold text-emerald-700">نصيب المستثمرين (70% من المحفظة):</p>
                            <p className="font-bold text-emerald-600">{remaining.toLocaleString(undefined, {maximumFractionDigits: 2})} ج.م</p>
                          </div>
                          {investors.map(inv => {
                            const pct = investorPcts[inv.id] !== undefined ? investorPcts[inv.id] : inv.profit_percentage;
                            const invAmount = remaining * pct / 100;
                            return (
                              <div key={inv.id} className="flex justify-between items-center py-1 border-b border-slate-100">
                                <span className="text-sm">{inv.full_name}</span>
                                <div className="flex items-center gap-2">
                                  <Input type="number" step="0.01" className="w-20 h-8 text-center text-sm"
                                    value={investorPcts[inv.id] !== undefined ? investorPcts[inv.id] : inv.profit_percentage}
                                    onChange={e => setInvestorPcts(prev => ({ ...prev, [inv.id]: parseFloat(e.target.value) || 0 }))}
                                    data-testid={`inv-pct-${inv.id}`} />
                                  <span className="text-xs text-slate-400">%</span>
                                  <span className="font-bold text-emerald-600 text-sm w-28 text-left">
                                    {invAmount.toLocaleString(undefined, {maximumFractionDigits: 2})} ج.م
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                          {(() => {
                            const totalInvPct = investors.reduce((s, i) => s + (investorPcts[i.id] !== undefined ? investorPcts[i.id] : i.profit_percentage), 0);
                            const isValid = Math.abs(totalInvPct - 100) < 0.01;
                            return (
                              <div className={`mt-2 p-2 rounded-lg text-sm font-bold ${isValid ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                                مجموع نسب المستثمرين: {totalInvPct.toFixed(1)}% {isValid ? '= 100% ✓' : '(يجب أن يساوي 100%)'}
                                <span className="block text-xs font-normal mt-1 opacity-75">100% من نسب المستثمرين = 70% من المحفظة = {remaining.toLocaleString(undefined, {maximumFractionDigits: 2})} ج.م</span>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  );
                })()}

                <Button onClick={handleDistribute} disabled={saving || !distTotal}
                  className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700 h-12 text-lg font-bold" data-testid="distribute-btn">
                  {saving ? <RefreshCw className="ml-2 h-5 w-5 animate-spin" /> : <DollarSign className="ml-2 h-5 w-5" />}
                  توزيع الأرباح
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ========== REQUESTS TAB ========== */}
          <TabsContent value="requests">
            <div className="space-y-6">
              {/* Deposits */}
              <Card className="bg-white shadow-lg border-0">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg"><ArrowDownCircle className="h-5 w-5 text-emerald-600" />طلبات الإيداع{pendingDeposits.length > 0 && <Badge className="bg-red-500 text-white mr-2">{pendingDeposits.length} معلق</Badge>}</CardTitle>
                </CardHeader>
                <CardContent>
                  {allRequests.deposits?.length === 0 ? <p className="text-center py-8 text-slate-400">لا توجد طلبات</p> : (
                    <div className="overflow-x-auto"><Table><TableHeader><TableRow>
                      <TableHead className="text-right font-bold">الشريك</TableHead><TableHead className="text-right font-bold">المبلغ</TableHead><TableHead className="text-right font-bold">الطريقة</TableHead><TableHead className="text-right font-bold">الحالة</TableHead><TableHead className="text-right font-bold">الصورة</TableHead><TableHead className="text-right font-bold">التاريخ</TableHead><TableHead className="text-right font-bold">إجراءات</TableHead>
                    </TableRow></TableHeader><TableBody>
                      {allRequests.deposits.map(r => (
                        <TableRow key={r.id} data-testid="admin-deposit-row">
                          <TableCell className="font-bold">{r.partner_name}</TableCell><TableCell>{r.requested_amount?.toLocaleString()} ج.م</TableCell><TableCell>{methodLabel(r.payment_method)}</TableCell><TableCell>{statusBadge(r.status)}</TableCell>
                          <TableCell>{r.image_path ? <Button size="sm" variant="ghost" onClick={() => handleViewImage(r.image_path)}><Image className="h-4 w-4" /></Button> : '-'}</TableCell>
                          <TableCell className="text-sm">{new Date(r.created_at).toLocaleDateString('ar-EG')}</TableCell>
                          <TableCell>{r.status === 'pending' && (<div className="flex gap-1"><Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 h-8 w-8 p-0" onClick={() => handleRequestAction('deposit', r.id, 'approved')} data-testid={`approve-dep-${r.id}`}><Check className="h-4 w-4" /></Button><Button size="sm" variant="destructive" className="h-8 w-8 p-0" onClick={() => handleRequestAction('deposit', r.id, 'rejected')}><X className="h-4 w-4" /></Button></div>)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody></Table></div>
                  )}
                </CardContent>
              </Card>
              {/* Withdrawals */}
              <Card className="bg-white shadow-lg border-0">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg"><ArrowUpCircle className="h-5 w-5 text-blue-600" />طلبات السحب{pendingWithdrawals.length > 0 && <Badge className="bg-red-500 text-white mr-2">{pendingWithdrawals.length} معلق</Badge>}</CardTitle>
                </CardHeader>
                <CardContent>
                  {allRequests.withdrawals?.length === 0 ? <p className="text-center py-8 text-slate-400">لا توجد طلبات</p> : (
                    <div className="overflow-x-auto"><Table><TableHeader><TableRow>
                      <TableHead className="text-right font-bold">الشريك</TableHead><TableHead className="text-right font-bold">المبلغ</TableHead><TableHead className="text-right font-bold">الطريقة</TableHead><TableHead className="text-right font-bold">رقم الحساب</TableHead><TableHead className="text-right font-bold">الحالة</TableHead><TableHead className="text-right font-bold">التاريخ</TableHead><TableHead className="text-right font-bold">إجراءات</TableHead>
                    </TableRow></TableHeader><TableBody>
                      {allRequests.withdrawals.map(r => (
                        <TableRow key={r.id} data-testid="admin-withdrawal-row">
                          <TableCell className="font-bold">{r.partner_name}</TableCell><TableCell>{r.requested_amount?.toLocaleString()} ج.م</TableCell><TableCell>{methodLabel(r.payment_method)}</TableCell><TableCell className="font-mono text-sm">{r.account_number || '-'}</TableCell><TableCell>{statusBadge(r.status)}</TableCell>
                          <TableCell className="text-sm">{new Date(r.created_at).toLocaleDateString('ar-EG')}</TableCell>
                          <TableCell>{r.status === 'pending' && (<div className="flex gap-1"><Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 h-8 w-8 p-0" onClick={() => handleRequestAction('withdrawal', r.id, 'approved')}><Check className="h-4 w-4" /></Button><Button size="sm" variant="destructive" className="h-8 w-8 p-0" onClick={() => handleRequestAction('withdrawal', r.id, 'rejected')}><X className="h-4 w-4" /></Button></div>)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody></Table></div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ========== HISTORY TAB ========== */}
          <TabsContent value="history">
            <div className="space-y-6">
              {/* Distribution logs summary */}
              <Card className="bg-white shadow-lg border-0">
                <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><History className="h-5 w-5" />سجل التوزيعات</CardTitle></CardHeader>
                <CardContent>
                  {distHistory.length === 0 ? <p className="text-center py-8 text-slate-400">لا يوجد سجل</p> : (
                    <Table><TableHeader><TableRow>
                      <TableHead className="text-right font-bold">الفترة</TableHead><TableHead className="text-right font-bold">إجمالي الربح</TableHead><TableHead className="text-right font-bold">عدد الشركاء</TableHead><TableHead className="text-right font-bold">التاريخ</TableHead>
                    </TableRow></TableHeader><TableBody>
                      {distHistory.map(h => (
                        <TableRow key={h.id}>
                          <TableCell className="font-bold">{monthNames[(h.month || 1) - 1]} {h.year}</TableCell>
                          <TableCell className="text-emerald-600 font-bold">{(h.total_profit || 0).toLocaleString()} ج.م</TableCell>
                          <TableCell>{h.partners_count}</TableCell>
                          <TableCell className="text-sm">{new Date(h.created_at).toLocaleDateString('ar-EG')}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody></Table>
                  )}
                </CardContent>
              </Card>

              {/* Per-partner records management */}
              <Card className="bg-white shadow-lg border-0">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg"><Edit className="h-5 w-5 text-blue-600" />إدارة سجلات الشركاء</CardTitle>
                  <CardDescription>اختر شريك ونوع السجل لعرض وتعديل وحذف أو إضافة</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg">
                    <div>
                      <Label className="text-sm font-bold">الشريك</Label>
                      <select value={selectedPartnerRecords} onChange={e => setSelectedPartnerRecords(e.target.value)}
                        className="w-full mt-1 h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="records-partner-select">
                        <option value="">-- اختر شريك --</option>
                        {partners.map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="text-sm font-bold">نوع السجل</Label>
                      <select value={recordTypeFilter} onChange={e => setRecordTypeFilter(e.target.value)}
                        className="w-full mt-1 h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="records-type-select">
                        <option value="all">الكل</option>
                        <option value="profits">أرباح</option>
                        <option value="deposits">إيداعات</option>
                        <option value="withdrawals">سحوبات</option>
                      </select>
                    </div>
                  </div>

                  {selectedPartnerRecords && (
                    <>
                      <div className="flex justify-between items-center pt-2">
                        <h3 className="font-bold text-slate-700">سجلات: {partners.find(p => p.id === selectedPartnerRecords)?.full_name}</h3>
                        <Dialog open={addRecordOpen} onOpenChange={setAddRecordOpen}>
                          <DialogTrigger asChild>
                            <Button size="sm" className="rounded-full bg-blue-600 hover:bg-blue-700" data-testid="add-record-btn">
                              <Plus className="ml-1 h-4 w-4" />إضافة سجل أرباح
                            </Button>
                          </DialogTrigger>
                          <DialogContent dir="rtl" className="max-w-sm">
                            <DialogHeader><DialogTitle>إضافة سجل أرباح</DialogTitle></DialogHeader>
                            <form onSubmit={handleAddRecord} className="space-y-3">
                              <div><Label>المبلغ</Label><Input name="amount" type="number" step="0.01" required className="mt-1" data-testid="record-amount" /></div>
                              <div className="grid grid-cols-2 gap-2">
                                <div><Label>السنة</Label><Input name="year" type="number" defaultValue={new Date().getFullYear()} required className="mt-1" /></div>
                                <div><Label>الشهر</Label>
                                  <select name="month" defaultValue={new Date().getMonth() + 1} className="w-full mt-1 h-10 rounded-md border border-input bg-background px-3 text-sm">
                                    {monthNames.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                                  </select>
                                </div>
                              </div>
                              <div><Label>الوصف (اختياري)</Label><Input name="description" className="mt-1" placeholder="أرباح شهر..." /></div>
                              <Button type="submit" className="w-full rounded-full bg-blue-600 hover:bg-blue-700" data-testid="submit-record">إضافة</Button>
                            </form>
                          </DialogContent>
                        </Dialog>
                      </div>

                      {recordsLoading ? (
                        <div className="flex justify-center py-8"><RefreshCw className="h-6 w-6 animate-spin text-blue-600" /></div>
                      ) : partnerRecords.length === 0 ? (
                        <p className="text-center py-8 text-slate-400">لا توجد سجلات</p>
                      ) : (
                        <Table>
                          <TableHeader><TableRow>
                            <TableHead className="text-right font-bold">النوع</TableHead>
                            <TableHead className="text-right font-bold">الوصف / التفاصيل</TableHead>
                            <TableHead className="text-right font-bold">المبلغ</TableHead>
                            <TableHead className="text-right font-bold">الحالة</TableHead>
                            <TableHead className="text-right font-bold">التاريخ</TableHead>
                            <TableHead className="text-right font-bold">إجراءات</TableHead>
                          </TableRow></TableHeader>
                          <TableBody>
                            {partnerRecords.map(r => {
                              const typeLabels = { profit: 'أرباح', deposit: 'إيداع', withdrawal: 'سحب' };
                              const typeColors = { profit: 'bg-emerald-100 text-emerald-700 border-emerald-300', deposit: 'bg-blue-100 text-blue-700 border-blue-300', withdrawal: 'bg-amber-100 text-amber-700 border-amber-300' };
                              const amount = r.record_type === 'profit' ? r.amount : r.requested_amount;
                              const desc = r.record_type === 'profit'
                                ? (r.description || `أرباح ${monthNames[(r.month || 1) - 1]} ${r.year}`)
                                : `${methodLabel(r.payment_method)}${r.account_number ? ' - ' + r.account_number : ''}`;
                              return (
                                <TableRow key={r.id} data-testid={`record-row-${r.id}`}>
                                  <TableCell><Badge className={`border ${typeColors[r.record_type]}`}>{typeLabels[r.record_type]}</Badge></TableCell>
                                  <TableCell className="text-sm">{desc}</TableCell>
                                  <TableCell>
                                    {editRecordId === r.id ? (
                                      <div className="flex items-center gap-1">
                                        <Input type="number" step="0.01" className="w-24 h-8" value={editRecordAmount}
                                          onChange={e => setEditRecordAmount(e.target.value)}
                                          onKeyDown={e => { if (e.key === 'Enter') handleUpdateRecord(r.id); if (e.key === 'Escape') setEditRecordId(null); }}
                                          autoFocus />
                                        <Button size="sm" className="h-8 w-8 p-0 bg-emerald-500" onClick={() => handleUpdateRecord(r.id)}><Check className="h-3 w-3" /></Button>
                                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setEditRecordId(null)}><X className="h-3 w-3" /></Button>
                                      </div>
                                    ) : (
                                      <span className="font-bold text-emerald-600">{(amount || 0).toLocaleString()} ج.م</span>
                                    )}
                                  </TableCell>
                                  <TableCell>{r.status ? statusBadge(r.status) : <Badge className="border bg-slate-100 text-slate-600 border-slate-300">مكتمل</Badge>}</TableCell>
                                  <TableCell className="text-sm text-slate-500">{new Date(r.created_at).toLocaleDateString('ar-EG')}</TableCell>
                                  <TableCell>
                                    <div className="flex gap-1">
                                      {r.record_type === 'profit' && (
                                        <Button size="sm" variant="ghost" onClick={() => { setEditRecordId(r.id); setEditRecordAmount(String(r.amount || 0)); }}>
                                          <Edit className="h-4 w-4 text-blue-500" />
                                        </Button>
                                      )}
                                      <Button size="sm" variant="ghost" onClick={() => handleDeleteRecord(r)}>
                                        <Trash2 className="h-4 w-4 text-red-500" />
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          {/* ========== CAPITAL TAB ========== */}
          <TabsContent value="capital">
            <Card className="bg-white shadow-lg border-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg"><Landmark className="h-5 w-5 text-amber-600" />جدول رأس المال الشهري</CardTitle>
                <CardDescription>أدخل رأس المال لكل شريك لحساب النسب المقترحة لتوزيع الأرباح</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-4 max-w-md">
                  <div>
                    <Label>السنة</Label>
                    <Input type="number" value={capYear} onChange={e => setCapYear(parseInt(e.target.value))} className="mt-1" data-testid="cap-year" />
                  </div>
                  <div>
                    <Label>الشهر</Label>
                    <select value={capMonth} onChange={e => setCapMonth(parseInt(e.target.value))} className="w-full mt-1 h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="cap-month">
                      {monthNames.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                    </select>
                  </div>
                </div>

                {capLoading ? (
                  <div className="flex justify-center py-8"><RefreshCw className="h-6 w-6 animate-spin text-amber-600" /></div>
                ) : (
                  <>
                    {(() => {
                      const totalCap = Object.values(capEntries).reduce((s, v) => s + (parseFloat(v) || 0), 0);
                      return (
                        <>
                          {totalCap > 0 && (
                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex justify-between items-center">
                              <span className="font-bold text-amber-800">إجمالي رأس المال:</span>
                              <span className="font-black text-amber-700 text-xl">{totalCap.toLocaleString()} ج.م</span>
                            </div>
                          )}
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="text-right font-bold">الشريك</TableHead>
                                  <TableHead className="text-right font-bold">النوع</TableHead>
                                  <TableHead className="text-right font-bold">رأس المال (ج.م)</TableHead>
                                  <TableHead className="text-right font-bold">النسبة المحسوبة</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {partners.map(p => {
                                  const val = capEntries[p.id] !== undefined ? capEntries[p.id] : '';
                                  const pct = totalCap > 0 ? ((parseFloat(val) || 0) / totalCap * 100) : 0;
                                  return (
                                    <TableRow key={p.id} data-testid={`cap-row-${p.id}`}>
                                      <TableCell className="font-bold">{p.full_name}</TableCell>
                                      <TableCell><Badge className={`border ${TYPE_COLORS[p.partner_type] || TYPE_COLORS.investor}`}>{TYPE_LABELS[p.partner_type] || 'مستثمر'}</Badge></TableCell>
                                      <TableCell>
                                        <Input
                                          type="number"
                                          step="0.01"
                                          className="w-40 h-9"
                                          value={val}
                                          onChange={e => setCapEntries(prev => ({ ...prev, [p.id]: parseFloat(e.target.value) || 0 }))}
                                          placeholder="0"
                                          data-testid={`cap-input-${p.id}`}
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <span className={`font-bold text-lg ${pct > 0 ? 'text-emerald-600' : 'text-slate-300'}`}>
                                          {pct > 0 ? `${pct.toFixed(2)}%` : '-'}
                                        </span>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </div>

                          {/* Investor-only summary */}
                          {investors.length > 0 && totalCap > 0 && (() => {
                            const investorCap = investors.reduce((s, inv) => s + (parseFloat(capEntries[inv.id]) || 0), 0);
                            return investorCap > 0 ? (
                              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 space-y-2">
                                <p className="font-bold text-emerald-800">النسب المقترحة للمستثمرين (لتوزيع الأرباح):</p>
                                <p className="text-xs text-emerald-600 mb-2">هذه النسب محسوبة من رأس مال المستثمرين فقط (بدون المؤسسين) ومجموعها = 100%</p>
                                <div className="space-y-1">
                                  {investors.map(inv => {
                                    const invCap = parseFloat(capEntries[inv.id]) || 0;
                                    const suggestedPct = investorCap > 0 ? (invCap / investorCap * 100) : 0;
                                    return (
                                      <div key={inv.id} className="flex justify-between items-center py-1 border-b border-emerald-100 last:border-0">
                                        <span className="text-sm font-medium">{inv.full_name}</span>
                                        <div className="flex items-center gap-3">
                                          <span className="text-sm text-slate-500">{(invCap).toLocaleString()} ج.م</span>
                                          <span className="font-bold text-emerald-700 bg-emerald-100 px-3 py-1 rounded-full text-sm" data-testid={`cap-suggested-${inv.id}`}>{suggestedPct.toFixed(2)}%</span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                                <div className="text-xs text-slate-500 mt-2 pt-2 border-t border-emerald-200">
                                  المجموع: {investors.reduce((s, inv) => {
                                    const invCap = parseFloat(capEntries[inv.id]) || 0;
                                    return s + (investorCap > 0 ? (invCap / investorCap * 100) : 0);
                                  }, 0).toFixed(2)}% = 100% (70% من محفظة المستثمرين)
                                </div>
                              </div>
                            ) : null;
                          })()}
                        </>
                      );
                    })()}
                    <Button onClick={handleSaveCapital} disabled={capSaving || Object.keys(capEntries).length === 0}
                      className="w-full rounded-full bg-amber-600 hover:bg-amber-700 h-12 text-lg font-bold" data-testid="save-capital-btn">
                      {capSaving ? <RefreshCw className="ml-2 h-5 w-5 animate-spin" /> : <Save className="ml-2 h-5 w-5" />}
                      حفظ بيانات رأس المال
                    </Button>
                    {capSaved && (
                      <p className="text-center text-sm text-emerald-600 font-medium">آخر حفظ: إجمالي {capSaved.total_capital?.toLocaleString()} ج.م</p>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ========== NOTIFICATIONS TAB ========== */}
          <TabsContent value="notifications">
            <div className="space-y-6">
              {/* Send notification */}
              <Card className="bg-white shadow-lg border-0">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg"><Send className="h-5 w-5 text-blue-600" />إرسال إشعار</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Private notification */}
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
                    <p className="font-bold text-blue-800 text-sm">إشعار خاص لشريك</p>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                      <select value={notifPartner} onChange={e => setNotifPartner(e.target.value)}
                        className="h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="notif-partner-select">
                        <option value="">اختر شريك</option>
                        {partners.map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
                      </select>
                      <select value={notifType} onChange={e => setNotifType(e.target.value)}
                        className="h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="notif-type-select">
                        <option value="general">إشعار عام</option>
                        <option value="profit">أرباح</option>
                        <option value="deposit">إيداع</option>
                        <option value="withdrawal">سحب</option>
                      </select>
                      <Input placeholder="نص الإشعار..." value={notifMessage} onChange={e => setNotifMessage(e.target.value)}
                        className="sm:col-span-2" data-testid="notif-message" />
                    </div>
                    <Button onClick={handleSendNotification} disabled={notifSending || !notifPartner || !notifMessage.trim()}
                      size="sm" className="rounded-full bg-blue-600 hover:bg-blue-700" data-testid="send-notif-btn">
                      <Send className="ml-1 h-4 w-4" />إرسال
                    </Button>
                  </div>

                  {/* Broadcast */}
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
                    <p className="font-bold text-amber-800 text-sm">إشعار لجميع الشركاء</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <select value={broadcastType} onChange={e => setBroadcastType(e.target.value)}
                        className="h-10 rounded-md border border-input bg-background px-3 text-sm">
                        <option value="general">إشعار عام</option>
                        <option value="profit">أرباح</option>
                        <option value="deposit">إيداع</option>
                        <option value="withdrawal">سحب</option>
                      </select>
                      <Input placeholder="نص الإشعار للجميع..." value={broadcastMessage} onChange={e => setBroadcastMessage(e.target.value)}
                        className="sm:col-span-2" data-testid="broadcast-message" />
                    </div>
                    <Button onClick={handleBroadcast} disabled={notifSending || !broadcastMessage.trim()}
                      size="sm" className="rounded-full bg-amber-600 hover:bg-amber-700" data-testid="broadcast-btn">
                      <Bell className="ml-1 h-4 w-4" />إرسال للجميع
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* All notifications list */}
              <Card className="bg-white shadow-lg border-0">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg"><Bell className="h-5 w-5" />جميع الإشعارات</CardTitle>
                </CardHeader>
                <CardContent>
                  {allNotifications.length === 0 ? <p className="text-center py-8 text-slate-400">لا توجد إشعارات</p> : (
                    <Table>
                      <TableHeader><TableRow>
                        <TableHead className="text-right font-bold">الشريك</TableHead>
                        <TableHead className="text-right font-bold">الإشعار</TableHead>
                        <TableHead className="text-right font-bold">التاريخ</TableHead>
                        <TableHead className="text-right font-bold">إجراءات</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {allNotifications.map(n => (
                          <TableRow key={n.id}>
                            <TableCell className="font-bold text-sm">{n.partner_name}</TableCell>
                            <TableCell className="text-sm">{n.message}</TableCell>
                            <TableCell className="text-sm text-slate-500">{new Date(n.created_at).toLocaleDateString('ar-EG')}</TableCell>
                            <TableCell>
                              <Button size="sm" variant="ghost" onClick={() => handleDeleteNotification(n.id)}>
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ========== ADD PROFIT TAB ========== */}
          <TabsContent value="add-profit">
            <Card className="bg-white shadow-lg border-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg"><DollarSign className="h-5 w-5 text-emerald-600" />إضافة ربح شهري لشريك</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <Label className="font-bold text-sm">الشريك</Label>
                    <select value={profitPartner} onChange={e => setProfitPartner(e.target.value)}
                      className="w-full h-10 mt-1 rounded-md border border-input bg-background px-3 text-sm" data-testid="profit-partner-select">
                      <option value="">اختر شريك</option>
                      {partners.map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="font-bold text-sm">المبلغ</Label>
                    <Input type="number" step="0.01" placeholder="0.00" value={profitAmount}
                      onChange={e => setProfitAmount(e.target.value)} className="mt-1" data-testid="profit-amount" />
                  </div>
                  <div>
                    <Label className="font-bold text-sm">الشهر</Label>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      <select value={profitMonth} onChange={e => setProfitMonth(parseInt(e.target.value))}
                        className="h-10 rounded-md border border-input bg-background px-3 text-sm">
                        {['يناير','فبراير','مارس','أبريل','مايو','يونيو','يوليو','أغسطس','سبتمبر','أكتوبر','نوفمبر','ديسمبر']
                          .map((m, i) => <option key={i} value={i+1}>{m}</option>)}
                      </select>
                      <Input type="number" value={profitYear} onChange={e => setProfitYear(parseInt(e.target.value))} />
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-4 bg-slate-50 rounded-xl p-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={profitSendNotif} onChange={e => setProfitSendNotif(e.target.checked)}
                      className="w-4 h-4 rounded" data-testid="profit-send-notif" />
                    <span className="text-sm font-bold">إرسال إشعار</span>
                  </label>
                  {profitSendNotif && (
                    <select value={profitNotifType} onChange={e => setProfitNotifType(e.target.value)}
                      className="h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="profit-notif-type">
                      <option value="profit">يظهر في الأرباح</option>
                      <option value="deposit">يظهر في الإيداعات</option>
                      <option value="withdrawal">يظهر في السحوبات</option>
                      <option value="general">إشعار عام</option>
                    </select>
                  )}
                </div>

                <Button onClick={handleAddIndividualProfit} disabled={profitSaving || !profitPartner || !profitAmount}
                  className="rounded-full bg-emerald-600 hover:bg-emerald-700 px-8" data-testid="add-profit-btn">
                  {profitSaving ? 'جاري الحفظ...' : 'إضافة الربح'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>

        {/* ========== EDIT PARTNER DIALOG ========== */}
        <Dialog open={editOpen} onOpenChange={(o) => { setEditOpen(o); if (!o) setEditPartner(null); }}>
          <DialogContent dir="rtl" className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Edit className="h-5 w-5" />تعديل بيانات الشريك</DialogTitle></DialogHeader>
            {editPartner && (
              <form onSubmit={handleEditPartner} className="space-y-4">
                <div><Label className="font-bold">الاسم الكامل</Label><Input name="full_name" defaultValue={editPartner.full_name} required className="mt-1" data-testid="edit-fullname" /></div>

                {/* Credentials Section */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
                  <p className="font-bold text-blue-800 flex items-center gap-2"><KeyRound className="h-4 w-4" />بيانات الدخول</p>
                  <div>
                    <Label className="text-sm">اسم المستخدم</Label>
                    <Input name="username" defaultValue={editPartner.username} required className="mt-1 bg-white" data-testid="edit-username" />
                  </div>
                  <div>
                    <Label className="text-sm">كلمة المرور الجديدة</Label>
                    <Input name="new_password" type="text" placeholder="اتركه فارغاً للإبقاء على الحالية" className="mt-1 bg-white" data-testid="edit-password" />
                    {editPartner.password && <p className="text-xs text-blue-600 mt-1">الحالية: <span className="font-mono font-bold">{editPartner.password}</span></p>}
                  </div>
                </div>

                <div>
                  <Label className="font-bold">نوع الشريك</Label>
                  <select name="partner_type" defaultValue={editPartner.partner_type} className="w-full mt-1 h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="edit-partner-type">
                    <option value="founder">شريك مؤسس</option>
                    <option value="investor">شريك مستثمر</option>
                  </select>
                </div>
                <div><Label className="font-bold">رأس المال</Label><Input name="initial_capital" type="number" step="0.01" defaultValue={editPartner.initial_capital} className="mt-1" data-testid="edit-capital" /></div>
                <div>
                  <Label className="font-bold">النسبة (%) <span className="text-xs text-slate-400 font-normal">- للمستثمرين</span></Label>
                  <Input name="profit_percentage" type="number" step="0.01" defaultValue={editPartner.profit_percentage} className="mt-1" data-testid="edit-percentage" />
                </div>
                <div><Label className="font-bold">تاريخ بداية الأرباح</Label><Input name="profit_start_date" type="date" defaultValue={editPartner.profit_start_date} className="mt-1" data-testid="edit-start-date" /></div>
                <Button type="submit" disabled={saving} className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700 h-11 font-bold" data-testid="submit-edit-partner">
                  {saving ? 'جاري الحفظ...' : 'حفظ التعديلات'}
                </Button>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Image Viewer */}
        <Dialog open={!!imageViewUrl} onOpenChange={() => { if (imageViewUrl) URL.revokeObjectURL(imageViewUrl); setImageViewUrl(null); }}>
          <DialogContent dir="rtl" className="max-w-2xl"><DialogHeader><DialogTitle>صورة التحويل</DialogTitle></DialogHeader>{imageViewUrl && <img src={imageViewUrl} alt="صورة" className="w-full rounded-lg" />}</DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

function methodLabel(m) { return { instapay: 'انستاباي', vodafone_cash: 'فودافون كاش', bank_transfer: 'تحويل بنكي', cash: 'نقدي' }[m] || m; }
function statusBadge(s) {
  const c = { pending: 'bg-amber-100 text-amber-700 border-amber-300', approved: 'bg-emerald-100 text-emerald-700 border-emerald-300', rejected: 'bg-red-100 text-red-700 border-red-300' };
  const l = { pending: 'قيد المراجعة', approved: 'مقبول', rejected: 'مرفوض' };
  return <Badge className={`${c[s] || c.pending} border`}>{l[s] || s}</Badge>;
}
